const pendingModuleLoads = new Map<string, Promise<unknown>>();

function loadModuleOnce<T>(key: string, importer: () => Promise<T>): Promise<T> {
  if (pendingModuleLoads.has(key)) return pendingModuleLoads.get(key) as Promise<T>;
  const p = importer().finally(() => pendingModuleLoads.delete(key));
  pendingModuleLoads.set(key, p);
  return p;
}

export const SUPPORTED_DOCUMENT_EXTENSIONS = ['txt', 'md', 'markdown', 'docx', 'pdf'];

export function isSupportedDocumentFile(name: string): boolean {
  const ext = name.split('.').pop()?.toLowerCase();
  return SUPPORTED_DOCUMENT_EXTENSIONS.includes(ext || '');
}

export function filterSupportedDocumentFiles(fileList: FileList | File[]): File[] {
  return Array.from(fileList).filter((f) => isSupportedDocumentFile(f.name));
}

export async function parseDocumentFile(file: File): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'txt' || ext === 'md' || ext === 'markdown') {
    return await file.text();
  }
  if (ext === 'docx') {
    const mammoth = await loadModuleOnce('mammoth', () => import('mammoth'));
    const ab = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer: ab });
    return result.value || '';
  }
  if (ext === 'pdf') {
    const pdfjsLib = await loadModuleOnce('pdfjs', () => import('pdfjs-dist'));
    if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
      // Vite rewrites this to the hashed self-hosted asset (pdf.worker.min-<hash>.mjs).
      // No CDN fallback: the worker version must match the bundled pdfjs-dist API exactly.
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url
      ).toString();
    }
    const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
    let text = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map((it) => ('str' in it ? it.str : '')).join(' ') + '\n';
    }
    return text;
  }
  return '';
}
